import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ParamsPanel } from '@/features/generator/ParamsPanel.js';
import { PalettePicker } from '@/features/palettes/PalettePicker.js';
import { ShareButton } from '@/features/presets/ShareButton.js';
import { useHydrateFromSlug } from '@/features/presets/useHydrateFromSlug.js';
import { Viewer2D } from '@/features/viewer-2d/Viewer2D.js';
import { TwoDModePicker } from '@/features/viewer-2d/ModePicker.js';
import { useHeightmap } from '@/lib/useHeightmap.js';
import { useViewStore } from '@/stores/viewStore.js';

const Viewer3D = lazy(() => import('@/features/viewer-3d/index.js').then(m => ({ default: m.Viewer3D })));

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Workspace />} />
      <Route path="/world/:slug" element={<Workspace />} />
    </Routes>
  );
}

function Workspace() {
  useHydrateFromSlug();
  const { heightmap, generating, version } = useHeightmap();
  const renderMode = useViewStore((s) => s.renderMode);

  return (
    <div className="h-full flex">
      <ParamsPanel />
      <main className="flex-1 flex flex-col">
        <header className="flex items-center justify-between border-b border-neutral-800 px-4 py-2">
          <h1 className="text-sm font-semibold tracking-wide">World Generation</h1>
          <div className="flex items-center gap-3">
            {renderMode === '2d' && <TwoDModePicker />}
            {generating && (
              <span
                role="status"
                aria-live="polite"
                className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-xs font-medium text-sky-300"
              >
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
                Generating…
              </span>
            )}
            <ShareButton />
          </div>
        </header>
        <div className="flex-1 grid place-items-center p-6 bg-neutral-950">
          {renderMode === '2d' && <Viewer2D heightmap={heightmap} />}
          {renderMode !== '2d' && (
            <Suspense fallback={<div className="text-neutral-400 text-sm">loading 3D…</div>}>
              <Viewer3D heightmap={heightmap} version={version} />
            </Suspense>
          )}
        </div>
        <PalettePicker />
      </main>
    </div>
  );
}
